import React, { useState } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import ProjectList from './ProjectList';
import './styles.css';

import { RSButton as RSButtonRaw, type RSButtonProps } from 'reactsymbols-kit';
import Styles from '../../styles/colors';
import Modal from '../Modal';
import CreateModelForm from './CreateModelForm';
import FirstTimeHelp from './FirstTimeHelp';
import confirmRemoveProject from './confirmation';
import { useStores } from '../../hooks/useStores';

// reactsymbols-kit types `RSButton` as `React.HTMLProps<HTMLButtonElement> &
// RSButtonProps`. `HTMLProps` pulls in `AllHTMLAttributes.size?: number`, which
// collides with the kit's intended `RSButtonProps.size?: string` and collapses
// the merged `size` to an unusable type — so `size="large"` is rejected. Re-type
// the component to the kit props intersected with `ButtonHTMLAttributes` (which
// has NO `size`, but does carry `onClick`/`type`/`disabled`), restoring the
// real string `size`. Type-only boundary cast; the runtime component is the
// same `RSButton`.
const RSButton = RSButtonRaw as unknown as React.ComponentType<
  RSButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>;

/** The identifier of a dashboard tab. */
type TabKey = 'RECENT' | 'MINE';

const tabs: Record<TabKey, string> = {
  RECENT: 'recent',
  MINE: 'mine',
};

const tabTitles: Record<TabKey, string> = {
  RECENT: 'Recent models',
  MINE: 'Created by me',
};

const modalTypes = {
  CREATE: 'create',
  IMPORT: 'import',
};

/**
 * The dashboard view on /
 *
 * The projects store was injected with mobx-react (`inject(({ store }) => ({
 * projects: store.projects }))`); it now comes from useStores(). The two state
 * fields (activeTab, modal) each get their own useState slot — the class did
 * single-field `setState` partial merges, which map cleanly to plain setters.
 */
export default observer(function Dashboard() {
  const store = useStores();
  const projects = store.projects;

  const [activeTab, setActiveTab] = useState(tabs.RECENT);
  const [modal, setModal] = useState<string | null>(null);

  // Handlers

  const switchTab = (key: TabKey) => {
    setActiveTab(tabs[key]);
  };

  const openModal = (type: string) => {
    setModal(type);
  };

  const closeModal = () => {
    setModal(null);
  };

  const removeProject = (id: string) => {
    const project = projects.getProjectById(id);
    if (project != null) {
      confirmRemoveProject(project.name).then(() => {
        projects.removeProject(id);
      });
    }
  };

  // Rendering

  /**
   * Get the list of projects based on the active tab.
   */
  const projectList = () => {
    switch (activeTab) {
      case tabs.MINE:
        return projects.myProjects;
      case tabs.RECENT:
      default:
        return projects.recentProjects;
    }
  };

  /**
   * Get the empty message based on the active tab.
   */
  const emptyMessage = () => {
    switch (activeTab) {
      case tabs.MINE:
        return 'No active models created by you.';
      case tabs.RECENT:
      default:
        return 'No recent models.';
    }
  };

  /**
   * Render a tab.
   *
   * @param key The key of the tab to render
   */
  const renderTab = (key: TabKey) => {
    const isActive = activeTab === tabs[key];
    return (
      <div
        key={key}
        className={classNames('p-[15px] cursor-pointer', {
          'border-b-[3px] border-primary': isActive,
        })}
        onClick={() => switchTab(key)}
      >
        {tabTitles[key]}
      </div>
    );
  };

  /**
   * Render the tabs
   */
  const renderTabs = () => {
    return (
      <div className="flex shadow-[inset_0_-1px_0_0_var(--color-grey3)]">
        {(Object.keys(tabs) as TabKey[]).map((key) => renderTab(key))}
      </div>
    );
  };

  /**
   * Render the buttons for create and import.
   *
   * @param first Is it the first model?
   */
  const renderCreateImportButtons = (first: boolean) => {
    return (
      <div className="flex flex-col gap-[10px]">
        <RSButton
          size="large"
          background={Styles.blueGradient}
          value={first ? 'Create your first MERODE model' : 'Start new model'}
          onClick={() => openModal(modalTypes.CREATE)}
        />

        <RSButton
          size="large"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          value={'Import existing model'}
          onClick={() => openModal(modalTypes.IMPORT)}
        />
      </div>
    );
  };

  /**
   * Render the list of projects
   */
  const renderProjectList = () => {
    return (
      <div className="flex-1 flex flex-col h-full mr-[30px] bg-white border border-grey">
        {renderTabs()}
        <ProjectList
          // `projectList()` is `IProject[] | null` because `myProjects` returns
          // the raw (nullable) `projects` array; the dashboard only renders this
          // list once projects are loaded (`count !== 0`), matching the store's
          // own "only read once loaded" invariant. Assert non-null — type-only.
          projects={projectList()!}
          emptyMessage={emptyMessage()}
          onDelete={removeProject}
        />
      </div>
    );
  };

  /**
   * Render the dashboard
   */
  const firstTime = projects.count === 0;
  return (
    <div
      className={classNames('h-full flex p-[30px]', {
        'items-start justify-between': !firstTime,
        'flex-col items-stretch justify-center gap-8 max-w-[600px] mx-auto': firstTime,
      })}
    >
      {/* Render either the first time here help or the list of projects */}
      {firstTime ? <FirstTimeHelp /> : renderProjectList()}
      {renderCreateImportButtons(firstTime)}
      {/* Render the modal for import or create */}
      <Modal isOpen={modal !== null} onRequestClose={closeModal}>
        <CreateModelForm
          import={modal === modalTypes.IMPORT}
          onRequestClose={closeModal}
          createProject={projects.createProject}
        />
      </Modal>
    </div>
  );
});
